import { useCallback, useEffect, useState } from "react";
import { Search, Eye } from "lucide-react";
import { useAuthedGet } from "@/pages/Admin";
import { fmtDate } from "@/lib/api";
import { TranscriptDialog } from "@/components/admin/TranscriptDialog";

const FILTERS = [["all", "Mind"], ["live", "Élő"], ["ended", "Befejezett"]];

const duration = (c) => {
  const end = c.ended_at ? new Date(c.ended_at) : new Date();
  const s = Math.max(0, Math.round((end - new Date(c.started_at)) / 1000));
  return s < 60 ? `${s} mp` : `${Math.floor(s / 60)} p ${s % 60} mp`;
};

export const ConversationsPanel = () => {
  const req = useAuthedGet();
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);

  const load = useCallback(() => {
    const params = {};
    if (filter !== "all") params.status = filter;
    if (q.trim()) params.q = q.trim();
    req("/admin/conversations", { params }).then((r) => setRows(r.data)).catch(() => {});
  }, [filter, q]); // eslint-disable-line

  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [load]);

  return (
    <div className="rounded-3xl border border-white/10 bg-[#1A152E]">
      <div className="flex flex-col gap-3 border-b border-white/10 p-4 md:flex-row md:items-center md:justify-between">
        <div className="flex gap-2">
          {FILTERS.map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`admin-filter-${k}`}
              className={`rounded-full px-4 py-1.5 text-xs font-bold transition-colors ${filter === k ? "bg-[#00F0FF] text-[#0F0C1B]" : "bg-white/5 hover:bg-white/10"}`}>{l}</button>
          ))}
        </div>
        <label className="flex items-center gap-2 rounded-full bg-white/5 px-4 py-2">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Keresés becenévre" data-testid="admin-search-input"
            className="w-48 bg-transparent text-sm outline-none" />
        </label>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-conversations-table">
          <thead className="text-left text-xs uppercase tracking-widest text-muted-foreground">
            <tr>{["Állapot", "Résztvevők", "Kezdés", "Időtartam", "Üzenet", "Fájl", ""].map((h) => <th key={h} className="px-4 py-3 font-bold">{h}</th>)}</tr>
          </thead>
          <tbody>
            {rows === null && <tr><td colSpan={7} className="px-4 py-10 text-center text-muted-foreground" data-testid="admin-conversations-loading">Betöltés…</td></tr>}
            {rows?.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-muted-foreground" data-testid="admin-empty">Még nincs beszélgetés.</td></tr>}
            {rows?.map((c) => (
              <tr key={c.id} data-testid={`admin-conversation-row-${c.id}`} className="border-t border-white/5 transition-colors hover:bg-white/[0.03]">
                <td className="px-4 py-3">
                  <span className={`rounded-full px-3 py-1 text-xs font-bold ${c.status === "live" ? "bg-[#00E676]/15 text-[#00E676]" : "bg-white/5 text-muted-foreground"}`}>
                    {c.status === "live" ? "Élő" : "Befejezett"}
                  </span>
                </td>
                <td className="px-4 py-3 font-semibold">{c.participants.map((p) => p.nick).join(" ↔ ")}</td>
                <td className="px-4 py-3 text-muted-foreground">{fmtDate(c.started_at)}</td>
                <td className="px-4 py-3 text-muted-foreground">{duration(c)}</td>
                <td className="px-4 py-3">{c.message_count}</td>
                <td className="px-4 py-3">{c.attachment_count}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => setOpen(c.id)} data-testid="admin-view-transcript-button"
                    className="inline-flex items-center gap-1.5 rounded-full bg-[#7C3AED] px-4 py-1.5 text-xs font-bold text-white transition-transform hover:scale-105">
                    <Eye className="h-3.5 w-3.5" /> Részletek
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <TranscriptDialog id={open} onClose={() => { setOpen(null); load(); }} />
    </div>
  );
};
